import { useState, useEffect, useMemo } from 'react'
import { View, Text, Pressable, Modal, ScrollView, StyleSheet } from 'react-native'
import { useTranslation } from 'react-i18next'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { supabase } from '../../lib/supabase'
import { colors, fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { useAvatars } from '../../lib/useAvatars'
import PersonAvatarFace from '../PersonAvatarFace'
import { stripTeamBracket } from '../Pick/ui'
import { loadRiderInfo } from '../Pick/riderInfo'
import { fetchOdrResults, fetchStageResults, fmtGap, OUT_OF_RANK } from '../Pick/odrResults'
import RiderRow from './RiderRow'

// Dettaglio di una squadra dalla Classifica: i componenti (con volto e distacco), chi conta per il team e le scelte di ognuno,
// nello stesso disegno della scheda giocatore (maglia della squadra, bandiera, piazzamento del corridore).
// team: { id, name, pos?, value? }
export default function TeamPickModal({ team, idStage, leagueId, raceId, isOneDay, onClose }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const insets = useSafeAreaInsets()
  const [data, setData] = useState(null)     // null = in caricamento
  const memberIds = (data?.members ?? []).map(m => m.id_user)
  const avatars = useAvatars(memberIds)

  useEffect(() => {
    if (!team || !idStage || !leagueId) return
    let cancelled = false
    ;(async () => {
      const { data: rows } = await supabase.rpc('fn_team_pick_stage', { p_id_league: leagueId, p_id_stage: idStage, p_id_league_team: team.id })
      if (cancelled) return
      // righe piatte (utente + slot) -> una scheda per utente
      const members = []; const byUser = {}
      for (const row of rows ?? []) {
        if (!byUser[row.id_user]) {
          byUser[row.id_user] = { id_user: row.id_user, display_name: row.display_name, gap_stage: row.gap_stage, conta_per_team: row.conta_per_team, riders: [] }
          members.push(byUser[row.id_user])
        }
        if (row.id_slot != null) byUser[row.id_user].riders.push({ slot: row.id_slot, is_flip: row.is_flip })
      }
      const userIds = members.map(m => m.id_user)
      // corridore effettivo di ogni slot (come view_effective_picks: tiene conto del flip)
      const [picksRes, catRes] = await Promise.all([
        userIds.length ? supabase.from('view_effective_picks').select('id_user, id_slot, id_rider').eq('id_stage', idStage).eq('id_league', leagueId).in('id_user', userIds) : Promise.resolve({ data: [] }),
        raceId ? supabase.from('dim_race').select('id_race_category').eq('id_race', raceId).maybeSingle() : Promise.resolve({ data: null }),
      ])
      const riderOf = {}
      ;(picksRes.data ?? []).forEach(p => { (riderOf[p.id_user] ??= {})[p.id_slot] = p.id_rider })
      const ids = [...new Set((picksRes.data ?? []).map(p => p.id_rider))].filter(Boolean)
      const [riders, res] = await Promise.all([
        loadRiderInfo(ids),
        isOneDay ? fetchOdrResults(idStage, ids) : fetchStageResults(idStage, ids),
      ])
      if (cancelled) return
      members.forEach(m => m.riders.forEach(r => {
        r.id = riderOf[m.id_user]?.[r.slot]
        if (isOneDay) { r.rank = res.ranks[r.id] ?? null; r.pts = r.rank != null ? (res.scores[r.slot]?.[r.rank] ?? OUT_OF_RANK) : null }
        else { const x = res.results[r.id]; r.rank = x?.rank ?? null; r.gap = x?.gap ?? null; r.dnf = !!x?.dnf }
      }))
      setData({ members, riders, national: catRes.data?.id_race_category === 5 })
    })().catch(() => { if (!cancelled) setData({ members: [], riders: {}, national: false }) })
    return () => { cancelled = true }
  }, [team, idStage, leagueId, raceId, isOneDay])

  const nameOf = (rd) => rd ? (data?.national ? stripTeamBracket(rd.labelNew || rd.label) : (rd.labelNew || rd.label)) : '—'

  return (
    <Modal visible transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityRole="button" accessibilityLabel={t('pick.close')} />
        <View style={[styles.sheet, { backgroundColor: tc.bgCard, paddingBottom: insets.bottom + 12 }]}>
          <View style={[styles.header, { borderBottomColor: tc.border }]}>
            <View style={[styles.teamIcon, { backgroundColor: tc.rosaSubtle }]}>
              <MaterialCommunityIcons name="account-group-outline" size={24} color={tc.rosaText} />
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={[styles.title, { color: tc.text }]} numberOfLines={1}>{team.name}</Text>
              {(team.pos || team.value) ? (
                <Text style={[styles.sub, { color: tc.textMuted }]} numberOfLines={1}>
                  {[team.pos ? `${team.pos}°` : null, team.value].filter(Boolean).join(' · ')}
                </Text>
              ) : null}
            </View>
            <Pressable onPress={onClose} hitSlop={6} style={[styles.close, { backgroundColor: tc.bgCard2 }]} accessibilityRole="button" accessibilityLabel={t('pick.close')}>
              <MaterialCommunityIcons name="close" size={18} color={tc.text} />
            </Pressable>
          </View>

          <ScrollView contentContainerStyle={{ padding: 12, gap: 12 }}>
            {data === null ? (
              <Text style={[styles.empty, { color: tc.textMuted }]}>{t('common.loading')}</Text>
            ) : data.members.length === 0 ? (
              <Text style={[styles.empty, { color: tc.textMuted }]}>{t('leaderboard.team_no_data')}</Text>
            ) : data.members.map(m => (
              <View key={m.id_user} style={[styles.member, { borderColor: m.conta_per_team ? tc.rosaBorder : tc.border }]}>
                <View style={styles.memberHead}>
                  <PersonAvatarFace userId={m.id_user} settings={avatars[m.id_user]} size={34} />
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={[styles.memberName, { color: tc.text }]} numberOfLines={1}>{m.display_name}</Text>
                    <View style={styles.countRow}>
                      <MaterialCommunityIcons name={m.conta_per_team ? 'check-circle-outline' : 'minus-circle-outline'} size={13} color={m.conta_per_team ? tc.rosaText : tc.textMuted} />
                      <Text style={[styles.countText, { color: m.conta_per_team ? tc.rosaText : tc.textMuted }]}>
                        {m.conta_per_team ? t('leaderboard.counts_for_team') : t('leaderboard.not_counting')}
                      </Text>
                    </View>
                  </View>
                  {!isOneDay && <Text style={[styles.memberGap, { color: tc.text }]}>{fmtGap(m.gap_stage) || '0:00'}</Text>}
                </View>
                <View style={{ gap: 6, marginTop: 10 }}>
                  {m.riders.map(r => {
                    const rd = data.riders[r.id]
                    return (
                      <RiderRow
                        key={r.slot}
                        slot={r.slot}
                        rd={rd}
                        national={data.national}
                        name={nameOf(rd)}
                        dim={!m.conta_per_team}
                        tags={r.is_flip ? (
                          <View style={styles.flipTag}>
                            <MaterialCommunityIcons name="swap-horizontal" size={12} color={colors.flip} />
                          </View>
                        ) : null}
                        right={r.rank != null ? (
                          <View style={styles.result}>
                            <Text style={[styles.rank, { color: tc.text }]}>{r.rank}°</Text>
                            {isOneDay
                              ? <Text style={[styles.pts, { color: r.pts > 0 ? colors.success : tc.textMuted }]}>{r.pts > 0 ? `+${r.pts}` : r.pts} pt</Text>
                              : !!fmtGap(r.gap) && <Text style={[styles.gap, { color: tc.textMuted }]}>{fmtGap(r.gap)}</Text>}
                          </View>
                        ) : (
                          <Text style={[styles.gap, { color: tc.textMuted }]}>{r.dnf ? t('pick.result_dnf') : '—'}</Text>
                        )}
                      />
                    )
                  })}
                </View>
              </View>
            ))}
          </ScrollView>
        </View>
      </View>
    </Modal>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'flex-end' },
  sheet: { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '85%' },
  header: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderBottomWidth: 0.5 },
  teamIcon: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center' },
  title: { fontFamily: fonts.condensedBold, fontSize: 20 },
  sub: { fontSize: 12, marginTop: 1 },
  close: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  empty: { fontSize: 13, textAlign: 'center', paddingVertical: 24 },
  member: { borderWidth: 1, borderRadius: 14, padding: 10 },
  memberHead: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  memberName: { fontFamily: fonts.condensedBold, fontSize: 16 },
  countRow: { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 1 },
  countText: { fontSize: 11, fontFamily: fonts.condensedBold },
  memberGap: { fontFamily: fonts.condensedBold, fontSize: 15 },
  flipTag: { flexDirection: 'row', alignItems: 'center', gap: 3, marginTop: 3 },
  result: { alignItems: 'flex-end', minWidth: 54 },
  rank: { fontFamily: fonts.condensedBold, fontSize: 18 },
  pts: { fontFamily: fonts.condensedBold, fontSize: 12 },
  gap: { fontSize: 11 },
})
